import React from 'react'
import type {
  GetSecretIdAndBeenViewedByShortIdQuery,
  PublicVideoRequest,
} from 'apollo-gql'
import cn from 'classnames'
import { useMarketContext } from 'contexts/MarketContext'
import { useTranslation } from 'lib/i18n'
import trackVideoRequestBookAgainClicked from 'lib/tracking/avo/trackVideoRequestBookAgainClicked'

import Link from 'components/Link/Link'
import { Button } from 'components/UI/Button/Button'
import useMarket from 'hooks/useMarket'
import useResponsive from 'hooks/useResponsive'

import styles from './VideoActionButtons.module.css'

type VideoActionButtonsProps = {
  review?: GetSecretIdAndBeenViewedByShortIdQuery['market']['review']
  request: PublicVideoRequest
  showReviewButton: boolean
}

const VideoActionButtons: React.FC<VideoActionButtonsProps> = ({
  review,
  request,
  showReviewButton,
}) => {
  const { t } = useTranslation()
  const { detectedMarket } = useMarketContext()
  const market = useMarket()
  const { isMobile } = useResponsive()

  const businessPage =
    market?.config?.businessPage?.[detectedMarket.language] ||
    Object.values(market?.config?.businessPage)?.[0] ||
    '/page/business'
  const { publicProfile: profile, product } = request
  const { secretId } = review || {}

  const handleBookAgain = () => {
    trackVideoRequestBookAgainClicked({ request })
  }

  const isBusinessRequest = product?.slug === 'video-greeting-business'

  const isBusinessRequestInternal =
    product?.slug === 'video-request-business-internal'

  const BookAgainButton = () => (
    <Link href={`/profile/${profile.slug}`}>
      <Button
        size="large"
        type="primary"
        onClick={handleBookAgain}
        data-cy="videoRequestPageBookAgainButton"
      >
        {t('VideoRequestPage.bookAgainCtaButton', 'Book again')}
      </Button>
    </Link>
  )

  const BookAnotherTalentButton = () => (
    <Link href={businessPage}>
      <Button size="large" type="secondary">
        {t('VideoRequestPage.bookAnotherTalent', 'Book another talent')}
      </Button>
    </Link>
  )

  const ChatWithAnAgentButton = () => (
    <a href="mailto:b2b@memmo.me" target="_blank" rel="noreferrer">
      <Button size="large" type="primary">
        {t('VideoRequestPage.chatWithAgentCTA', 'Chat with an agent')}
      </Button>
    </a>
  )

  if (isBusinessRequestInternal) {
    if (isMobile) {
      return (
        <>
          <div className={styles.videoPageCtaButton}>
            <BookAgainButton />
          </div>
          <div className={styles.videoPageCtaButton}>
            <BookAnotherTalentButton />
          </div>
        </>
      )
    }
    return (
      <div className={styles.videoPageCtaButton}>
        <ChatWithAnAgentButton />
      </div>
    )
  }

  if (isBusinessRequest) {
    if (isMobile) {
      return (
        <>
          <div className={styles.videoPageCtaButton}>
            <BookAgainButton />
          </div>
          <div className={styles.videoPageCtaButton}>
            <BookAnotherTalentButton />
          </div>
        </>
      )
    }
    return (
      <>
        <div className={styles.videoPageCtaButton}>
          <ChatWithAnAgentButton />
        </div>
        <div className={styles.videoPageCtaButton}>
          <BookAnotherTalentButton />
        </div>
      </>
    )
  }

  return (
    <>
      <div
        className={cn(styles.videoPageCtaButton, {
          [styles.placeholder]: !showReviewButton,
        })}
      >
        <BookAgainButton />
      </div>

      {showReviewButton && (
        <Link href={`/review/${secretId}`}>
          <div className={styles.videoPageCtaButton}>
            <Button
              size="large"
              type="secondary"
              data-cy="videoRequestPageLeaveAReviewButton"
            >
              {t('VideoRequestPage.leaveAReview', 'Leave a review')}
            </Button>
          </div>
        </Link>
      )}
    </>
  )
}

export default VideoActionButtons
